import { Box, Card, Flex, Grid, Text } from '@chakra-ui/react'
import React, { useState, useEffect } from 'react'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import AvatarCell from './base/AvatarCell'
import AccessBar from './base/AccessBar'
import AccessRolesTable from './AccessRolesTable'
import { Button, HStack, Icon, useToast } from '@chakra-ui/react'
import { Send01Icon } from '../../assets/icons'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { inviteEmployee } from '../../api/workforce/workforce'
import handleToast from '../HandleToast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useRole } from '../../hooks/auth/useRole'


interface SettingsTableProps {
  selectedTab: string
  onSelectTab: (tab: string) => void
}

const SettingsTable: React.FC<SettingsTableProps> = ({ selectedTab, onSelectTab }) => {
  const { t } = useTranslation()
  const { isSuperAdmin } = useRole()
  const [filters, setFilters] = useState<{ [key: string]: any[] }>({ active: ['true'] })
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15
  const [selectedRows, setSelectedRows] = useState<(number | string)[]>([])
  const toast = useToast()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const people = useQuery({
    queryKey: ['people', page, searchTerm, filters],
    staleTime: 30 * 1000,
    queryFn: () =>
      fetchEmployeeList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters: { ...filters, active: ['true'] },
        // request only the fields required to render this table
        fields: 'id,first_name,last_name,profile_picture,role,status,created_at',
        // avoid unnecessary expands
        expand: '',
      }),
  })

  const inviteMutation = useMutation({
    mutationFn: (employee: { id: string }) => inviteEmployee(employee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] })
    },
  })

  const getInviteErrorMessage = (error: any): string => {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as any

      if (data?.code === 'INVITE_EXPIRED') {
        return t('workforce.messages.inviteExpired')
      }
      if (Array.isArray(data) && typeof data[0] === 'string') return data[0]
      if (typeof data === 'string') return data
      if (typeof data?.detail === 'string') return data.detail
      if (typeof data?.message === 'string') return data.message

      if (!error.response) {
        return t('workforce.messages.networkError')
      }

      return t('workforce.messages.requestFailed', { code: error.response.status })
    }

    return error?.message || t('workforce.messages.somethingWentWrong')
  }

  const handleInvite = () => {
    selectedRows.forEach((employeeId) => {
      const employee = filteredData.find((row: RowData) => row.id === employeeId)
      const displayName = `${employee?.first_name ?? ''} ${employee?.last_name ?? ''}`.trim() || t('common.fields.user')

      inviteMutation.mutate(
        { id: employeeId.toString() },
        {
          onSuccess: () => {
            handleToast(toast, true, t('workforce.messages.inviteSuccess'), t('workforce.messages.inviteSuccessMessage', { name: displayName }))
            setSelectedRows([])
          },
          onError: (e) => {
            handleToast(toast, false, t('workforce.messages.inviteFailed'), getInviteErrorMessage(e))
          },
        },
      )
    })
  }

  const handleRowClick = (row: RowData) => {
    navigate(`/user/workforce/people/${row.id}`, { state: { from: page } }) // pass the current page
  }


  const ROLE_LABELS: Record<string, string> = {
    ...(isSuperAdmin ? { super_admin: t('settings.access.roles.super_admin') } : {}),
    org_admin: t('settings.access.roles.org_admin'),
    finance: t('settings.access.roles.finance'),
    country_admin: t('settings.access.roles.country_admin'),
    team_leader: t('settings.access.roles.team_leader'),
    user: t('common.fields.user'),
  }

  const ROLE_OPTIONS = [
    ...(isSuperAdmin ? [{ id: 'super_admin', name: t('settings.access.roles.super_admin') }] : []),
    { id: 'org_admin', name: t('settings.access.roles.org_admin') },
    { id: 'finance', name: t('settings.access.roles.finance') },
    { id: 'country_admin', name: t('settings.access.roles.country_admin') },
    { id: 'team_leader', name: t('settings.access.roles.team_leader') },
    { id: 'user', name: t('common.fields.user') },
  ]

  const STATUS_OPTIONS = [
    { id: 'not_invited', name: t('settings.access.status.not_invited') },
    { id: 'invited', name: t('settings.access.status.invited') },
    { id: 'invite_expired', name: t('settings.access.status.invite_expired') },
    { id: 'registered', name: t('settings.access.status.registered') },
  ]

  const columns: Column[] = [
    {
      label: '',
      accessor: 'select',
      type: 'select',
      width: '5%',
    },
    {
      label: t('settings.access.table.columns.adminName'),
      accessor: 'name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.profile_picture} name={`${row.first_name} ${row.last_name}`} />
      ),
    },

    {
      label: t('common.fields.role'),
      accessor: 'role',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {ROLE_LABELS[row.role] || row.role || t('common.labels.na')}
        </Text>
      ),
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const STATUS_LABELS: Record<string, string> = {
          not_invited: t('settings.access.status.not_invited'),
          invited: t('settings.access.status.invited'),
          invite_expired: t('settings.access.status.invite_expired'),
          registered: t('settings.access.status.registered'),
        }
        return (
          <Text textStyle="l-xs" isTruncated>
            {STATUS_LABELS[row.status] || row.status || t('common.labels.na')}
          </Text>
        )
      },
    },
    {
      label: t('settings.access.table.columns.dateAdded'),
      accessor: 'created_at',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.created_at
            ? new Date(row.created_at).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : t('common.labels.na')}
        </Text>
      ),
    },

  ]

  // Frontend filtering removed; use backend filtering only
  const filteredData = people.data?.data || []

  // Prefetch next page when current page looks full
  useEffect(() => {
    const currentCount = people.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return
    const nextPage = page + 1
    queryClient.prefetchQuery({
      queryKey: ['people', nextPage, searchTerm, filters],
      staleTime: 30 * 1000,
      queryFn: () =>
        fetchEmployeeList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters: { ...filters, active: ['true'] },
          fields: 'id,first_name,last_name,profile_picture,role,status,created_at',
          expand: '',
        }),
    })
  }, [people.data, page, searchTerm, filters, queryClient])

  const handleSearch = (searchTerm: string) => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <AccessBar selectedTab={selectedTab} onSelectTab={onSelectTab} />
          {selectedTab === 'admins' ? (
            <>
              <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
                <SearchInput onSearch={handleSearch} />
                <Flex gap="8px">
                  {selectedRows.length > 0 && (
                    <Button
                      onClick={handleInvite}
                      variant="white"
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                    >
                      <Icon w="12px" h="12px" mr={2}>
                        <Send01Icon />
                      </Icon>
                      {t('settings.access.actions.inviteCount', { count: selectedRows.length })}
                    </Button>
                  )}

                  <FilterButton label={t('common.fields.role')} options={ROLE_OPTIONS} onApply={(v) => updateFilter('role', v)} />
                  <FilterButton label={t('common.fields.status')} options={STATUS_OPTIONS} onApply={(v) => updateFilter('status', v)} />
                </Flex>
              </Flex>
              {people.isLoading ? (
                <LoadingTable columns={columns} />
              ) : (
                <Box minHeight="58vh">
                  <MasterTable
                    columns={columns}
                    onRowClick={handleRowClick}
                    data={filteredData}
                    selectedRows={selectedRows}
                    onSelectRow={(id: number | string, isChecked: boolean) => {
                      setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)))
                    }}
                    onSelectAll={(isChecked: boolean) => {
                      setSelectedRows(isChecked ? filteredData.map((r: RowData) => r.id) : [])
                    }}
                    allSelected={filteredData.length > 0 && selectedRows.length === filteredData.length}
                    isIndeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                    tableType="people"
                  />
                </Box>
              )}
              <Pagination
                currentPage={page}
                itemsPerPage={itemsPerPage}
                maxPage={people?.data?.max_page || 0}
                totalItems={people?.data?.count || 0}
                handlePreviousPage={() => setPage(page - 1)}
                handleNextPage={() => setPage(page + 1)}
                handlePageClick={(page: number) => setPage(page)}
              />
            </>
          ) : (
            <AccessRolesTable />
          )}
        </Box>
      </Grid>
    </Card>
  )
}

export default SettingsTable
